import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, Search, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";

const PAGES = {
  "immobilier-kinshasa": {
    title: "Immobilier à Kinshasa | Maisons, appartements, parcelles et locaux",
    description: "Trouvez à Kinshasa des maisons, villas, appartements, parcelles, immeubles et locaux à vendre ou à louer avec Team Work Africa.",
    kicker: "Immobilier à Kinshasa",
    h1: "Trouvez votre bien immobilier à Kinshasa.",
    intro: "Team Work Africa centralise des offres immobilières et accompagne les demandes d’achat, de location, de vente et de visite à Kinshasa.",
    bullets: ["Maisons et villas", "Appartements et studios", "Parcelles et terrains", "Immeubles, bureaux et locaux"],
    primary: "/explorer?sector=real_estate&city=Kinshasa",
    primaryLabel: "Voir les biens à Kinshasa",
    request: "/demandes/nouvelle?sector=immobilier&city=Kinshasa&context=1",
    universe: "/univers/immobilier",
    schemaType: "RealEstateAgent",
  },
  "location-voiture-kinshasa": {
    title: "Location de voiture à Kinshasa | Team Work Africa",
    description: "Recherchez une voiture, un SUV, un utilitaire ou autre véhicule à louer à Kinshasa avec Team Work Africa.",
    kicker: "Location voiture Kinshasa",
    h1: "Louez un véhicule à Kinshasa selon votre besoin.",
    intro: "Comparez les véhicules disponibles et décrivez votre besoin si vous ne trouvez pas encore le modèle adapté.",
    bullets: ["Voitures de ville", "SUV et 4x4", "Utilitaires", "Location courte ou longue durée selon l’offre"],
    primary: "/explorer?sector=auto&purpose=rent&city=Kinshasa",
    primaryLabel: "Voir les véhicules à louer",
    request: "/demandes/nouvelle?sector=auto&transaction=rent&city=Kinshasa&context=1",
    universe: "/univers/automobile",
    schemaType: "AutoRental",
  },
  "hotel-appartement-meuble-kinshasa": {
    title: "Hôtel et appartement meublé à Kinshasa | Team Work Africa",
    description: "Trouvez hôtels, appartements meublés, studios et résidences pour vos séjours à Kinshasa avec Team Work Africa.",
    kicker: "Séjours à Kinshasa",
    h1: "Trouvez votre hôtel ou appartement meublé à Kinshasa.",
    intro: "Team Work Africa vous aide à explorer des hébergements pour un court ou long séjour et à lancer une demande de réservation.",
    bullets: ["Hôtels", "Appartements meublés", "Studios", "Résidences"],
    primary: "/explorer?sector=hospitality&city=Kinshasa",
    primaryLabel: "Voir les hébergements",
    request: "/demandes/nouvelle?sector=hospitality&city=Kinshasa&context=1",
    universe: "/univers/sejours",
    schemaType: "LodgingBusiness",
  },
  "salle-mariage-kinshasa": {
    title: "Salle de mariage à Kinshasa | Réception et événement",
    description: "Trouvez une salle de mariage ou de réception à Kinshasa avec Team Work Africa et demandez disponibilité, capacité et conditions.",
    kicker: "Salle de mariage Kinshasa",
    h1: "Trouvez une salle de mariage ou de réception à Kinshasa.",
    intro: "Explorez les espaces disponibles ou décrivez votre événement pour que Team Work Africa vous aide à trouver une solution adaptée.",
    bullets: ["Salles de mariage", "Salles de réception", "Capacité adaptée à votre événement", "Demande de disponibilité et de devis"],
    primary: "/explorer?sector=real_estate&portal=espaces&category=salle_mariage&city=Kinshasa",
    primaryLabel: "Voir les salles",
    request: "/demandes/nouvelle?sector=professional&space=salle_mariage&city=Kinshasa&context=1",
    universe: "/univers/espaces",
    schemaType: "EventVenue",
  },
  "salle-conference-kinshasa": {
    title: "Salle de conférence à Kinshasa | Réunion et formation",
    description: "Trouvez une salle de conférence, réunion ou formation à Kinshasa avec Team Work Africa.",
    kicker: "Salle de conférence Kinshasa",
    h1: "Réservez un espace pour réunion, formation ou conférence à Kinshasa.",
    intro: "Team Work Africa vous aide à identifier un espace adapté au format, au nombre de participants et aux besoins de votre événement professionnel.",
    bullets: ["Salles de conférence", "Salles de réunion", "Salles de formation", "Coworking et bureaux privés"],
    primary: "/explorer?sector=real_estate&portal=espaces&category=salle_conference&city=Kinshasa",
    primaryLabel: "Voir les espaces",
    request: "/demandes/nouvelle?sector=professional&space=salle_conference&city=Kinshasa&context=1",
    universe: "/univers/espaces",
    schemaType: "EventVenue",
  },
  "coworking-kinshasa": {
    title: "Coworking à Kinshasa | Bureaux et espaces de travail",
    description: "Trouvez coworking, bureau privé, salle de réunion et espace de travail à Kinshasa avec Team Work Africa.",
    kicker: "Coworking Kinshasa",
    h1: "Trouvez un espace de travail à Kinshasa.",
    intro: "Explorez des espaces pour travailler seul, recevoir une équipe, organiser une réunion ou installer temporairement votre activité.",
    bullets: ["Coworking", "Bureaux privés", "Salles de réunion", "Espaces professionnels"],
    primary: "/explorer?sector=real_estate&portal=espaces&category=coworking&city=Kinshasa",
    primaryLabel: "Voir les espaces de travail",
    request: "/demandes/nouvelle?sector=professional&space=coworking&city=Kinshasa&context=1",
    universe: "/univers/espaces",
    schemaType: "ProfessionalService",
  },
  "services-kinshasa": {
    title: "Services à Kinshasa | Trouver un professionnel avec Team Work Africa",
    description: "Trouvez à Kinshasa des professionnels pour déménagement, nettoyage, décoration, photo, traiteur, sécurité, internet, solaire et plus.",
    kicker: "Services à Kinshasa",
    h1: "Trouvez un professionnel pour votre besoin à Kinshasa.",
    intro: "Décrivez votre besoin ou explorez les services disponibles. Team Work Africa qualifie la demande et facilite la mise en relation.",
    bullets: ["Déménagement et nettoyage", "Décoration, photo et vidéo", "Traiteur et événementiel", "Sécurité, internet, solaire et autres services"],
    primary: "/univers/services",
    primaryLabel: "Explorer les services",
    request: "/demandes/nouvelle?sector=services&city=Kinshasa&context=1",
    universe: "/univers/services",
    schemaType: "ProfessionalService",
  },
  "acheter-voiture-kinshasa": {
    title: "Acheter une voiture à Kinshasa | Team Work Africa",
    description: "Recherchez voitures, SUV, motos, utilitaires, camions et engins à vendre à Kinshasa avec Team Work Africa.",
    kicker: "Acheter une voiture à Kinshasa",
    h1: "Trouvez un véhicule à acheter à Kinshasa.",
    intro: "Explorez les véhicules disponibles à la vente et demandez à Team Work Africa de chercher pour vous si votre besoin est spécifique.",
    bullets: ["Voitures", "SUV et 4x4", "Motos", "Camions et engins"],
    primary: "/explorer?sector=auto&purpose=sale&city=Kinshasa",
    primaryLabel: "Voir les véhicules à vendre",
    request: "/demandes/nouvelle?sector=auto&transaction=buy&city=Kinshasa&context=1",
    universe: "/univers/automobile",
    schemaType: "AutoDealer",
  },
};

export default function LocalSeoLanding() {
  const { slug } = useParams();
  const page = PAGES[slug];
  if (!page) return <Navigate to="/" replace />;

  const canonical = `https://teamwork-africa.com/solutions/${slug}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.title,
    description: page.description,
    url: canonical,
    about: { "@type": "Thing", name: page.kicker },
    areaServed: { "@type": "City", name: "Kinshasa" },
    isPartOf: { "@id": "https://teamwork-africa.com/#website" },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://teamwork-africa.com/" },
        { "@type": "ListItem", position: 2, name: page.kicker, item: canonical },
      ],
    },
  };

  return (
    <>
      <Seo title={page.title} description={page.description} path={`/solutions/${slug}`} schema={schema} />
      <div className="bg-white">
        <section className="border-b border-slate-100 bg-gradient-to-br from-slate-50 via-white to-orange-50/40">
          <div className="container-app py-14 sm:py-20 lg:py-24">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-100 bg-white px-4 py-2 text-xs font-black uppercase tracking-[.16em] text-brand-orange">
                <MapPin className="h-4 w-4" /> {page.kicker}
              </div>
              <h1 className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[1.03] tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">{page.h1}</h1>
              <p className="mt-5 max-w-3xl text-base leading-7 text-slate-600 sm:text-lg">{page.intro}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to={page.primary} className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-brand-orange px-6 text-sm font-extrabold text-white">
                  <Search className="h-4 w-4" /> {page.primaryLabel}
                </Link>
                <Link to={page.request} className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 text-sm font-extrabold text-brand-navy">
                  Trouver pour moi <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="container-app py-14 sm:py-18">
          <div className="grid gap-8 lg:grid-cols-[1.15fr_.85fr]">
            <div>
              <h2 className="font-display text-3xl font-extrabold text-brand-navy">Ce que vous pouvez trouver avec Team Work Africa</h2>
              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {page.bullets.map((item) => (
                  <div key={item} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                    <span className="text-sm font-bold text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <aside className="rounded-3xl bg-brand-navy p-7 text-white">
              <ShieldCheck className="h-8 w-8 text-brand-orange" />
              <h2 className="mt-4 text-2xl font-black">Vous ne trouvez pas exactement ce que vous cherchez ?</h2>
              <p className="mt-3 text-sm leading-6 text-white/70">Décrivez votre besoin. Team Work Africa peut qualifier votre demande et vous orienter vers une offre ou un partenaire adapté.</p>
              <Link to={page.request} className="mt-6 inline-flex h-11 items-center gap-2 rounded-xl bg-white px-5 text-sm font-black text-brand-navy">Décrire mon besoin <ArrowRight className="h-4 w-4" /></Link>
            </aside>
          </div>
        </section>

        <section className="border-y border-slate-100 bg-slate-50">
          <div className="container-app py-12">
            <h2 className="font-display text-2xl font-extrabold text-brand-navy">Comment ça marche ?</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {[
                ["1", "Explorez ou décrivez votre besoin", "Utilisez les offres disponibles ou envoyez directement votre demande."],
                ["2", "Team Work qualifie la demande", "Les informations utiles sont vérifiées avant la mise en relation ou la suite du parcours."],
                ["3", "Passez à l’action", "Selon le cas : visite, réservation, devis, achat, location ou prise de contact."],
              ].map(([n,t,d]) => <article key={n} className="rounded-2xl bg-white p-6 ring-1 ring-slate-200"><div className="text-sm font-black text-brand-orange">{n}</div><h3 className="mt-2 font-black text-brand-navy">{t}</h3><p className="mt-2 text-sm leading-6 text-slate-600">{d}</p></article>)}
            </div>
            <div className="mt-8"><Link to={page.universe} className="inline-flex items-center gap-2 text-sm font-extrabold text-brand-orange">Voir l’univers complet <ArrowRight className="h-4 w-4" /></Link></div>
          </div>
        </section>
      </div>
    </>
  );
}
